import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Button,
  Switch,
  Container,
  Alert,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { Link, useNavigate, useLocation } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { remainingDays, formatJpDate } from "../../../../utils/dateUtils";
import ConfirmStopModal from "../../../../shared/ConfirmStopModal";
import { LevelTooltipText, levelMap } from "../../../../shared/Constants";

const ProjectDetailPage = () => {
  const navigate = useNavigate();

  // ルートパラメータからIDを取得
  const { id } = useParams();

  // プロジェクト情報のステート管理
  const [project, setProject] = useState(null);

  // テーマ情報とレスポンシブ判定
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openConfirm, setOpenConfirm] = useState(false);
  const [switchChecked, setSwitchChecked] = useState(false);

  //　メッセージ受けとる
  const location = useLocation();
  const flashMessage = location.state?.flashMessage;

  // textareaの改行設定
  const preLineLabels = [
    "備考",
    "概要",
    "スキル詳細",
    "工程詳細",
    "ポジション詳細",
    "商流詳細",
  ];

  // work_style の enum値と日本語ラベルを同期
  const WORK_STYLES = {
    on_site: "常駐",
    remote: "フルリモート",
    hybrid: "ハイブリッド",
  };

  // commercial_flow の enum値と日本語ラベルを同期
  const COMMERCIAL_FLOW = {
    end_client_company: "エンド直",
    primary_contractor_company: "元受け直",
    other: "その他",
  };

  // 初期表示でプロジェクトデータを取得（idが存在する場合）
  useEffect(() => {
    fetch(`/company/projects/${id}.json`)
      .then((res) => res.json())
      .then((data) => {
        setProject(data);
        setSwitchChecked(data.published_flag);
      })
      .catch((e) => {
        console.error("プロジェクトの取得中にエラーが発生しました", e);
      });
  }, [id]);

  const updatePublishedFlag = (newStatus) => {
    fetch(`/company/projects/${project.id}/toggle_published`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          ?.content,
      },
      body: JSON.stringify({ published_flag: newStatus }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "ok") {
          setSwitchChecked(newStatus);
          setProject((prev) => ({ ...prev, published_flag: newStatus }));
        } else {
          alert("公開ステータスの更新に失敗しました。");
        }
      })
      .catch((err) => {
        console.error("公開切替エラー:", err);
        alert("通信エラーが発生しました。");
      });
  };

  const handleSwitchChange = (e) => {
    if (!e.target.checked) {
      setOpenConfirm(true);
    } else {
      updatePublishedFlag(true);
    }
  };

  const handleConfirmUnpublish = () => {
    updatePublishedFlag(false);
    setOpenConfirm(false);
  };

  // 表示する行の定義
  const rows = useMemo(() => {
    if (!project) return [];

    return [
      { label: "プロジェクト名", value: project.name || "未設定" },
      { label: "概要", value: project.description || "未設定" },
      {
        label: "稼働期間",
        value: project.work_start_date
          ? project.long_term_flag
            ? // 長期フラグがtrueの場合
              `${formatJpDate(project.work_start_date)}〜長期`
            : // 長期フラグがfalseの場合
            project.work_end_date
            ? `${formatJpDate(project.work_start_date)}〜${formatJpDate(
                project.work_end_date
              )}`
            : `${formatJpDate(project.work_start_date)}〜未設定`
          : "未設定",
      },
      {
        label: "金額",
        value:
          project.price_min && project.price_max
            ? `${project.price_min}〜${project.price_max}万円`
            : "未設定",
      },
      {
        label: "想定稼働",
        value:
          project.workload_min && project.workload_max
            ? `${project.workload_min}〜${project.workload_max}h`
            : "未設定",
      },
      {
        label: "稼働形態",
        value: project.work_style
          ? WORK_STYLES[project.work_style] || "未設定"
          : "未設定",
      },
      // 稼働形態がリモート以外（=ハイブリッド or 常駐）なら作業場所を表示
      ...(project.work_style === "hybrid" || project.work_style === "on_site"
        ? [
            {
              label: "作業場所",
              value:
                Array.isArray(project.prefecture_names) &&
                project.prefecture_names.length > 0
                  ? project.prefecture_names.join("、")
                  : "未設定",
            },
          ]
        : []),
      {
        label: "商流",
        value: COMMERCIAL_FLOW[project.commercial_flow] || "未設定",
      },
      {
        label: "商流詳細",
        value: project.commercial_flow_detail || "なし",
      },
      {
        label: "スキル",
        value:
          Array.isArray(project.skills) && project.skills.length > 0
            ? project.skills.map((s) => {
                const year =
                  s.experience_year != null
                    ? `${s.experience_year}年以上`
                    : "年数未設定";
                const level =
                  s.level != null
                    ? `レベル${levelMap[s.level] || s.level}以上`
                    : "レベル未設定";
                return `${s.skill_name}（${year}、${level}）`;
              })
            : ["未設定"],
      },
      { label: "スキル詳細", value: project.skill_detail || "なし" },
      {
        label: "工程",
        value:
          Array.isArray(project.operations) && project.operations.length > 0
            ? project.operations
                .sort((a, b) => a.operation_id - b.operation_id)
                .map((p) => {
                  const year =
                    p.experience_year != null
                      ? `${p.experience_year}年以上`
                      : "年数未設定";
                  return `${p.operation_name}（${year}）`;
                })
            : ["未設定"],
      },
      { label: "工程詳細", value: project.operation_detail || "なし" },
      {
        label: "ポジション",
        value:
          Array.isArray(project.roles) && project.roles.length > 0
            ? project.roles.map((p) => {
                const year =
                  p.experience_year != null
                    ? `${p.experience_year}年以上`
                    : "年数未設定";
                return `${p.role_name}（${year}）`;
              })
            : ["未設定"],
      },
      { label: "ポジション詳細", value: project.role_detail || "なし" },
      {
        label: "面談回数",
        value: project.interview_count
          ? `${project.interview_count}`
          : "未設定",
      },
      { label: "備考", value: project.remark || "なし" },
    ];
  }, [project]);

  // データ未取得時のローディング表示
  if (project === null) {
    return (
      <Box p={4} ml={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* フラッシュメッセージ*/}
      {flashMessage && (
        <Box sx={{ px: 2, py: 1 }}>
          <Alert severity="success">
            {typeof flashMessage === "object"
              ? flashMessage.message
              : flashMessage}
          </Alert>
        </Box>
      )}

      <Container maxWidth="md" sx={{ mt: 4, mb: 10 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={4}
        >
          {/* 左：戻るリンク */}
          <Typography
            component={Link}
            to="/company/projects"
            sx={{
              fontSize: "1.25rem",
              fontWeight: "bold",
              textDecoration: "none",
              color: "inherit",
              "&:hover": {
                opacity: 0.7,
              },
            }}
          >
            ＜ プロジェクト一覧へ戻る
          </Typography>

          {/* 右：ボタン + 補足 */}
          <Box textAlign="right">
            <Box
              display="inline-flex"
              flexDirection="column"
              alignItems="flex-end"
            >
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  navigate(`/company/projects/new?duplicate_from=${project.id}`)
                }
                sx={{ fontWeight: "bold" }}
              >
                ＋ プロジェクトを複製する
              </Button>
              <Typography
                fontSize="0.6rem"
                color="text.secondary"
                mt={0.5}
                sx={{ whiteSpace: "normal", textAlign: "right" }}
              >
                この情報を元に新規投稿を作成できます。
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* 詳細本体エリア */}
        <Paper elevation={3} sx={{ borderRadius: 2, p: 4 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            {/* 左：状態テキスト + Switch */}
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontWeight="bold" variant="h6">
                プロジェクトは{switchChecked ? "公開中" : "非公開"}
              </Typography>
              <Switch
                color="secondary"
                checked={switchChecked}
                onChange={handleSwitchChange}
              />
            </Box>

            {/* 右：編集ボタン */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate(`/company/projects/${project.id}/edit`)}
            >
              編集する
            </Button>
          </Box>

          {/* 掲載終了日と残日数 */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography fontSize="12px" color="gray">
              掲載期間 {formatJpDate(project.publish_end_date)}まで
            </Typography>
            <Typography fontSize="12px" color="text.secondary">
              {remainingDays(project.publish_end_date)}
            </Typography>
          </Box>

          {/* タイトル */}
          <Typography variant="h5" fontWeight="bold" mb={2}>
            {project.title}
          </Typography>

          {/* 情報テーブル */}
          <Table size="small" sx={{ mb: 4, border: "1px solid #ccc" }}>
            <TableBody>
              {rows.map((row, index) =>
                isMobile ? (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#4b5563" : "#9ca3af",
                          color: "white",
                          py: 1,
                          fontSize: "0.8rem",
                        }}
                      >
                        {row.label}
                        {row.label === "スキル" && (
                          <Box>
                            <LevelTooltipText
                              fontSize={10}
                              color="rgba(255, 255, 255, 0.8)"
                            />
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#f3f4f6" : "#f9fafb",
                          fontSize: "0.8rem",
                          whiteSpace: preLineLabels.includes(row.label)
                            ? "pre-line"
                            : undefined,
                          wordBreak: preLineLabels.includes(row.label)
                            ? "break-all"
                            : undefined,
                        }}
                      >
                        {Array.isArray(row.value)
                          ? row.value.map((v, i) => <div key={i}>{v}</div>)
                          : row.value}
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ) : (
                  <TableRow key={index}>
                    <TableCell
                      align="right"
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#4b5563" : "#9ca3af",
                        color: "white",
                        width: "25%",
                      }}
                    >
                      {row.label}
                      {row.label === "スキル" && (
                        <Tooltip
                          title={<LevelTooltipText />}
                          arrow
                          placement="top"
                        >
                          <InfoOutlinedIcon
                            fontSize="small"
                            sx={{
                              ml: 0.5,
                              verticalAlign: "middle",
                              color: "white",
                              opacity: 0.7,
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#f3f4f6" : "#f9fafb",
                        whiteSpace: preLineLabels.includes(row.label)
                          ? "pre-line"
                          : undefined,
                        wordBreak: preLineLabels.includes(row.label)
                          ? "break-all"
                          : undefined,
                      }}
                    >
                      {Array.isArray(row.value)
                        ? row.value.map((v, i) => <div key={i}>{v}</div>)
                        : row.value}
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>

          <Box textAlign="center" mt={4}>
            <Button
              variant="contained"
              sx={{
                // 公開中→黒、非公開→紫
                backgroundColor: switchChecked ? "#111827" : "secondary.main",
                color: "white",
                borderRadius: 999,
                px: 4,
                py: 1.2,
                "&:hover": {
                  backgroundColor: switchChecked ? "#1f2937" : "secondary.dark",
                },
              }}
              onClick={() => {
                if (switchChecked) {
                  // 現在公開中 → モーダルで非公開確認
                  setOpenConfirm(true);
                } else {
                  // 現在非公開 → 即座に公開処理
                  updatePublishedFlag(true);
                }
              }}
            >
              {switchChecked
                ? "プロジェクトを非公開にする"
                : "プロジェクトを公開する"}
            </Button>
          </Box>
        </Paper>

        <ConfirmStopModal
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          onConfirm={handleConfirmUnpublish}
          title="プロジェクトの公開停止"
          message={
            <>
              非公開にするとエンジニア側から
              <br />
              プロジェクト情報の閲覧が不可となります。
            </>
          }
          confirmLabel="公開を停止する"
        />
      </Container>
    </>
  );
};

export default ProjectDetailPage;
